import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Typography,
  Upload,
  message,
  Spin,
} from "antd";
import {
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title } = Typography;
const apiUrl = process.env.REACT_APP_API_URL;

function WebsiteCms() {
  const [items, setItems] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null); // null = Add mode
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCmsCards = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${apiUrl}/api/marketingwebsite/show-cms-cards`
      );
      setItems(res.data.data);
    } catch (error) {
      message.error("Failed to fetch CMS cards");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCmsCards();
  }, []);

  const handleAddClick = () => {
    setEditingItem(null);
    form.resetFields();
    setFileList([]);
    setIsModalVisible(true);
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    form.setFieldsValue(item);
    if (item.imageUrl) {
      setFileList([
        {
          uid: "-1",
          name: item.imageUrl.split("/").pop(),
          status: "done",
          url: item.imageUrl,
        },
      ]);
    }
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `${apiUrl}/api/marketingwebsite/delete-cms-card/${id}`
      );
      message.success("Deleted successfully");
      fetchCmsCards();
    } catch {
      message.error("Failed to delete");
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("originalLink", values.originalLink);
      formData.append("description", values.description);
      if (fileList.length && fileList[0].originFileObj) {
        formData.append("imageUrl", fileList[0].originFileObj);
      }

      if (editingItem) {
        await axios.patch(
          `${apiUrl}/api/marketingwebsite/update-cms-card/${editingItem._id}`,
          formData
        );
        message.success("Card updated");
      } else {
        await axios.post(
          `${apiUrl}/api/marketingwebsite/create-cms-card`,
          formData
        );
        message.success("Card added");
      }

      setIsModalVisible(false);
      fetchCmsCards();
    } catch (err) {
      message.error("Failed to save card");
    }
  };

  return (
    <div>
      <Title level={3}>All Contents</Title>

      <div className="grid grid-cols-3 2xl:grid-cols-4 gap-[1rem]">
        <div
          className="bg-white flex aspect-[4/3] items-center justify-center border border-green-500 rounded-md hover:scale-105 transition-transform duration-300 cursor-pointer"
          onClick={handleAddClick}
        >
          + ADD
        </div>

        {items.map((item, i) => (
          <div
            key={item._id || i}
            className="bg-white px-[1rem] flex flex-col aspect-[4/3] py-[1.5rem] rounded-md shadow-sm hover:shadow-md transition-shadow duration-400"
          >
            <div className="flex-1">
              <div className="text-[1rem] font-medium">{item.title}</div>
              <p className="mt-[0.5rem] text-[#6b7280]">{item.description}</p>
            </div>
            <div className="flex justify-end gap-x-[1rem] text-blue-600 cursor-pointer">
              <div onClick={() => handleEditClick(item)}>
                <EditOutlined /> Edit
              </div>
              <div
                onClick={() => handleDelete(item._id)}
                className="text-red-400"
              >
                <DeleteOutlined />
                Delete
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSave}
        title={editingItem ? "Edit Content" : "Add Content"}
        okText="Save"
        destroyOnClose
      >
        <Spin spinning={loading}>
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: "Please enter title" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Original Link"
              name="originalLink"
              rules={[{ required: true, message: "Please enter link" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              rules={[{ required: true, message: "Please enter description" }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>

            <Form.Item label="Image Upload">
              <Upload
                listType="picture"
                fileList={fileList}
                beforeUpload={() => false}
                onChange={({ fileList }) => setFileList(fileList.slice(-1))}
              >
                <Button icon={<UploadOutlined />}>Upload</Button>
              </Upload>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
}

export default WebsiteCms;
